import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import distributorService from '../services/DistributorService';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { user, authState } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscription status states
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'active', 'expired', 'cancelled', 'pending', 'no_subscription'
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);

  const fetchSubscription = useCallback(async () => {
    // Only fetch for distributors
    if (user?.role !== 'distributor' || !authState.isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await distributorService.getMySubscription();

      if (result.success && result.data) {
        const subData = result.data;
        setSubscription(subData);
        setSubscriptionStatus(subData.status);
        setDaysRemaining(subData.days_remaining);
        setHasActiveSubscription(subData.status === 'active');

        // Show banner if subscription is expiring soon (within 7 days) or not active
        if (subData.status !== 'active' || (subData.days_remaining !== null && subData.days_remaining <= 7)) {
          setShowSubscriptionBanner(true);
        } else {
          setShowSubscriptionBanner(false);
        }
      } else {
        // No subscription found
        setSubscription(null);
        setSubscriptionStatus('no_subscription');
        setDaysRemaining(null);
        setHasActiveSubscription(false);
        setShowSubscriptionBanner(true);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to fetch subscription status');
      setSubscription(null);
      setSubscriptionStatus('no_subscription');
      setHasActiveSubscription(false);
    } finally {
      setLoading(false);
    }
  }, [user?.role, authState.isAuthenticated]);

  // Fetch subscription on mount and when user changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Refresh subscription data
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  // Check if subscription allows access
  const checkAccess = useCallback(() => {
    if (user?.role !== 'distributor') {
      return { allowed: true, reason: null };
    }

    if (loading) {
      return { allowed: true, reason: null }; // Allow during loading
    }

    if (subscriptionStatus === 'active') {
      return { allowed: true, reason: null };
    }

    if (subscriptionStatus === 'pending') {
      return {
        allowed: false,
        reason: 'pending',
        message: 'Your subscription is pending. Please complete payment to access this feature.'
      };
    }

    if (subscriptionStatus === 'expired') {
      return {
        allowed: false,
        reason: 'expired',
        message: 'Your subscription has expired. Please renew to continue.'
      };
    }

    if (subscriptionStatus === 'cancelled') {
      return {
        allowed: false,
        reason: 'cancelled',
        message: 'Your subscription was cancelled. Please subscribe to a plan to continue.'
      };
    }

    return {
      allowed: false,
      reason: 'no_subscription',
      message: 'No active subscription. Please subscribe to a plan to continue.'
    };
  }, [user?.role, loading, subscriptionStatus]);

  // Dismiss banner
  const dismissBanner = useCallback(() => {
    setShowSubscriptionBanner(false);
  }, []);

  // Get subscription status message
  const getStatusMessage = useCallback(() => {
    if (!subscription) {
      return {
        type: 'warning',
        title: 'No Subscription',
        message: 'You do not have an active subscription. Subscribe to a plan to access all features.'
      };
    }

    switch (subscriptionStatus) {
      case 'active':
        if (daysRemaining !== null && daysRemaining <= 7) {
          return {
            type: 'warning',
            title: 'Subscription Expiring Soon',
            message: `Your subscription expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Renew now to avoid service interruption.`
          };
        }
        return {
          type: 'success',
          title: 'Active Subscription',
          message: `Your ${subscription.plan_name} plan is active.`
        };
      case 'expired':
        return {
          type: 'danger',
          title: 'Subscription Expired',
          message: 'Your subscription has expired. Renew now to restore access.'
        };
      case 'cancelled':
        return {
          type: 'secondary',
          title: 'Subscription Cancelled',
          message: 'Your subscription was cancelled. Subscribe to a new plan to continue.'
        };
      case 'pending':
        return {
          type: 'info',
          title: 'Payment Pending',
          message: 'Your subscription is pending. Please complete payment to activate.'
        };
      default:
        return {
          type: 'warning',
          title: 'No Subscription',
          message: 'Subscribe to a plan to access all features.'
        };
    }
  }, [subscription, subscriptionStatus, daysRemaining]);

  const value = {
    subscription,
    loading,
    error,
    hasActiveSubscription,
    subscriptionStatus,
    daysRemaining,
    showSubscriptionBanner,
    refreshSubscription,
    checkAccess,
    dismissBanner,
    getStatusMessage
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
